// 📁 src/components/ProtectedRoute.jsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * 🔐 Extended ProtectedRoute
 * - Keeps ALL existing logic untouched.
 * - FIXES Pro access gating by using `user.plan` as source of truth.
 * - Billing (`subscription`) remains secondary / async.
 */
const ProtectedRoute = ({
  children,
  role,
  requiresPro = false,
  requiresMentorSubscription = false
}) => {
  const { user, token } = useAuth();
  const location = useLocation();

  // =====================================================================
  // 🚫 LITE ROUTES MUST NEVER BE PROTECTED (CRITICAL — UNCHANGED)
  // =====================================================================
  if (location.pathname.startsWith("/lite")) {
    return children;
  }

  // =====================================================================
  // 🔒 1) AUTH CHECK — UNCHANGED
  // =====================================================================
  if (!user || !token) {
    return <Navigate to="/login" replace />;
  }

  // =====================================================================
  // ⭐ 1.5) ROLE NORMALIZATION — UNCHANGED
  // =====================================================================
  const effectiveRole =
    user.role === "founder" ? "user" : user.role;

  // =====================================================================
  // 🔒 2) ROLE CHECK — UNCHANGED
  // =====================================================================
  if (role) {
    const allowedRoles = Array.isArray(role) ? role : [role];

    if (!allowedRoles.includes(effectiveRole)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // =====================================================================
  // ⭐⭐ 3) PRO ACCESS CHECK (FIXED — PLAN IS SOURCE OF TRUTH)
  // =====================================================================
  if (requiresPro === true) {
    if (user.role !== "admin") {
      const isPro =
        user?.plan === "pro" ||
        user?.subscription?.active === true;

      if (!isPro) {
        console.log("🔒 PRO module blocked — user not Pro");
        return <Navigate to="/pricing" state={{ from: location }} replace />;
      }
    }
  }

  // =====================================================================
  // ⭐⭐ 4) MENTOR PREMIUM SUBSCRIPTION CHECK — UNCHANGED
  // =====================================================================
  if (requiresMentorSubscription === true) {
    if (user.role === "mentor") {
      const isMentorPaid =
        user?.subscription?.active === true &&
        user?.subscription?.revenueStream === "mentor_subscription";

      if (!isMentorPaid) {
        return <Navigate to="/pricing/mentor" replace />;
      }
    }
  }

  // =====================================================================
  // ⭐ 5) MENTOR ACCESS CONTROL — UNCHANGED
  // =====================================================================
  if (user.role === "mentor") {
    if (user.status === "pending" && location.pathname !== "/mentor-pending") {
      return <Navigate to="/mentor-pending" replace />;
    }

    const mentorAllowedRoutes = ["/mentor-dashboard", "/mentor-pending"];
    const isAllowedForMentor = mentorAllowedRoutes.some((r) =>
      location.pathname.startsWith(r)
    );

    if (user.status === "approved" && !isAllowedForMentor) {
      return <Navigate to="/mentor-dashboard" replace />;
    }
  }

  // =====================================================================
  // ⭐ 6) INVESTOR ACCESS CONTROL — UNCHANGED
  // =====================================================================
  if (user.role === "investor") {
    const investorAllowedRoutes = ["/investor-dashboard"];
    const isAllowedInvestor = investorAllowedRoutes.some((r) =>
      location.pathname.startsWith(r)
    );

    if (!isAllowedInvestor) {
      return <Navigate to="/investor-dashboard" replace />;
    }
  }

  // =====================================================================
  // ⭐ 7) FOUNDER ACCESS CONTROL — FIXED (PLAN FIRST)
  // =====================================================================
  if (user.role === "founder") {
    const isPro =
      user?.plan === "pro" ||
      user?.subscription?.active === true;

    const lockedProModules = [
      "/market-research",
      "/mvp-planner",
      "/mvp-agent",
      "/business-model-builder",
      "/ai-testing",
      "/gtm-accelerator",
      "/pitch-deck",
      "/funding-match",
      "/dashboard"
    ];

    const isLocked = lockedProModules.some((path) =>
      location.pathname.startsWith(path)
    );

    if (isLocked && !isPro) {
      return <Navigate to="/lite/upgrade" replace />;
    }
  }

  // =====================================================================
  // ⭐ 8) ADMIN ALWAYS FULL ACCESS — UNCHANGED
  // =====================================================================

  return children;
};

export default ProtectedRoute;
